/**
 * P1.1: Email Event Dictionary
 * Single source of truth for all email events across the platform
 * 
 * BACKEND-READY STRUCTURE:
 * - All events are immutable keys
 * - Admins can edit content (subject/body) but NOT event keys
 * - Compatible with future JWT auth + email service integration
 * 
 * @version 1.0.0
 * @lastUpdated 2026-01-26
 */

// =============================================================================
// AUDIENCE TYPES
// =============================================================================
export const EMAIL_AUDIENCE = {
  CLINIC: 'CLINIC',       // Clinic admins and staff
  PATIENT: 'PATIENT',     // End customers/patients
  INTERNAL: 'INTERNAL',   // TreatX ops, admins, sales
};

// =============================================================================
// TRIGGER SOURCES
// =============================================================================
export const TRIGGER_SOURCE = {
  // Clinic lifecycle
  APPLICATION: 'application',
  SUBSCRIPTION: 'subscription',
  TRIAL: 'trial',
  
  // Patient lifecycle
  AUTH: 'auth',
  INTAKE: 'intake',
  MATCHING: 'matching',
  OFFER: 'offer',
  PAYMENT: 'payment',
  
  // Internal ops
  ADMIN: 'admin',
  SALES: 'sales',
};

// =============================================================================
// TEMPLATE VARIABLES REGISTRY
// =============================================================================
export const TEMPLATE_VARIABLES = {
  // Common
  supportEmail: { label: 'Support Email', example: 'support@treatx.health', scope: ['CLINIC', 'PATIENT', 'INTERNAL'] },
  platformUrl: { label: 'Platform URL', example: 'https://treatx.health', scope: ['CLINIC', 'PATIENT', 'INTERNAL'] },
  currentDate: { label: 'Current Date', example: 'January 26, 2026', scope: ['CLINIC', 'PATIENT', 'INTERNAL'] },
  
  // Clinic-specific
  clinicName: { label: 'Clinic Name', example: 'Istanbul Aesthetics Clinic', scope: ['CLINIC', 'INTERNAL'] },
  clinicEmail: { label: 'Clinic Email', example: 'admin@istanbul-aesthetics.com', scope: ['CLINIC', 'INTERNAL'] },
  clinicPhone: { label: 'Clinic Phone', example: '+90 212 555 1234', scope: ['CLINIC', 'INTERNAL'] },
  clinicCity: { label: 'Clinic City', example: 'Istanbul', scope: ['CLINIC', 'INTERNAL'] },
  clinicCountry: { label: 'Clinic Country', example: 'Turkey', scope: ['CLINIC', 'INTERNAL'] },
  
  // Application-specific
  applicationId: { label: 'Application ID', example: 'APP-2026-001234', scope: ['CLINIC', 'INTERNAL'] },
  applicationStatus: { label: 'Application Status', example: 'Approved', scope: ['CLINIC', 'INTERNAL'] },
  rejectionReason: { label: 'Rejection Reason', example: 'Invalid license number provided', scope: ['CLINIC'] },
  flaggedFields: { label: 'Flagged Fields', example: 'License Number, Business License Document', scope: ['CLINIC'] },
  reviewerName: { label: 'Reviewer Name', example: 'Alex Morgan', scope: ['CLINIC', 'INTERNAL'] },
  reviewDate: { label: 'Review Date', example: 'January 25, 2026', scope: ['CLINIC', 'INTERNAL'] },
  
  // Trial/Subscription-specific
  trialStartDate: { label: 'Trial Start Date', example: 'January 25, 2026', scope: ['CLINIC'] },
  trialEndDate: { label: 'Trial End Date', example: 'February 8, 2026', scope: ['CLINIC'] },
  trialDaysRemaining: { label: 'Trial Days Remaining', example: '3', scope: ['CLINIC'] },
  planName: { label: 'Plan Name', example: 'Professional', scope: ['CLINIC'] },
  planPrice: { label: 'Plan Price', example: '€99/month', scope: ['CLINIC'] },
  previousPlan: { label: 'Previous Plan', example: 'Starter', scope: ['CLINIC'] },
  
  // Patient-specific
  patientName: { label: 'Patient Name', example: 'Maria Schmidt', scope: ['PATIENT', 'INTERNAL'] },
  patientFirstName: { label: 'Patient First Name', example: 'Maria', scope: ['PATIENT'] },
  patientEmail: { label: 'Patient Email', example: 'maria.schmidt@email.com', scope: ['PATIENT', 'INTERNAL'] },
  patientPhone: { label: 'Patient Phone', example: '+49 170 555 1234', scope: ['PATIENT', 'INTERNAL'] },
  patientCountry: { label: 'Patient Country', example: 'Germany', scope: ['PATIENT', 'INTERNAL'] },
  
  // Auth-specific
  // TREATX-PRODUCTION-P0-OWNER-ACCEPTANCE-02 §P0-3: replace placeholder
  // tokens that read like real opaque session tokens (`abc123`, `xyz789`)
  // with self-describing `<sample-…>` strings so an owner reviewing a
  // template preview cannot mistake the example value for production
  // data. Verification *code* preview is left realistic-looking (6-digit
  // numeric) because that IS the canonical format the recipient sees.
  verificationLink: { label: 'Verification Link', example: 'https://treatx.health/verify?token=<sample-verification-token>', scope: ['PATIENT'] },
  resetPasswordLink: { label: 'Reset Password Link', example: 'https://treatx.health/reset?token=<sample-reset-token>', scope: ['PATIENT'] },
  verificationCode: { label: 'Verification Code', example: '485721 (sample)', scope: ['PATIENT'] },
  
  // Intake/Matching-specific
  intakeId: { label: 'Intake ID', example: 'INK-2026-005678', scope: ['PATIENT', 'INTERNAL'] },
  treatmentType: { label: 'Treatment Type', example: 'Hair Transplant', scope: ['PATIENT', 'CLINIC', 'INTERNAL'] },
  preferredDestination: { label: 'Preferred Destination', example: 'Turkey', scope: ['PATIENT', 'INTERNAL'] },
  matchCount: { label: 'Match Count', example: '5', scope: ['PATIENT'] },
  matchedClinics: { label: 'Matched Clinics', example: 'Istanbul Aesthetics, Ankara Medical Center', scope: ['PATIENT'] },
  
  // Offer/Payment-specific
  offerId: { label: 'Offer ID', example: 'OFF-2026-009012', scope: ['PATIENT', 'CLINIC', 'INTERNAL'] },
  offerAmount: { label: 'Offer Amount', example: '€3,500', scope: ['PATIENT', 'CLINIC'] },
  offerCurrency: { label: 'Offer Currency', example: 'EUR', scope: ['PATIENT', 'CLINIC'] },
  offerExpiry: { label: 'Offer Expiry Date', example: 'February 15, 2026', scope: ['PATIENT'] },
  depositAmount: { label: 'Deposit Amount', example: '€500', scope: ['PATIENT'] },
  depositDueDate: { label: 'Deposit Due Date', example: 'February 1, 2026', scope: ['PATIENT'] },
  paymentMethod: { label: 'Payment Method', example: 'Credit Card', scope: ['PATIENT'] },
  
  // Sales/Internal-specific
  salesRepName: { label: 'Sales Rep Name', example: 'Sarah Johnson', scope: ['CLINIC', 'INTERNAL'] },
  salesRepEmail: { label: 'Sales Rep Email', example: 'sarah@istanbul-aesthetics.com', scope: ['CLINIC', 'INTERNAL'] },
  leadScore: { label: 'Lead Score', example: '85', scope: ['INTERNAL'] },
  estimatedValue: { label: 'Estimated Value', example: '€5,000', scope: ['INTERNAL'] },
  alertReason: { label: 'Alert Reason', example: 'Multiple rejections in 24 hours', scope: ['INTERNAL'] },
};

// =============================================================================
// EMAIL EVENT DICTIONARY
// Single source of truth for all email events
// =============================================================================
export const EMAIL_EVENTS = {
  // =========================================================================
  // CATEGORY A: CLINIC / PROVIDER EVENTS
  // =========================================================================
  
  // Application Lifecycle
  CLINIC_APPLICATION_SUBMITTED: {
    eventKey: 'CLINIC_APPLICATION_SUBMITTED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.APPLICATION,
    category: 'application',
    displayName: 'Application Submitted',
    description: 'Sent when a clinic submits their registration application',
    defaultVariables: ['clinicName', 'clinicEmail', 'applicationId', 'supportEmail', 'platformUrl'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  CLINIC_APPLICATION_APPROVED: {
    eventKey: 'CLINIC_APPLICATION_APPROVED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.APPLICATION,
    category: 'application',
    displayName: 'Application Approved',
    description: 'Sent when admin approves a clinic application',
    defaultVariables: ['clinicName', 'applicationId', 'reviewerName', 'reviewDate', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  CLINIC_APPLICATION_REJECTED: {
    eventKey: 'CLINIC_APPLICATION_REJECTED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.APPLICATION,
    category: 'application',
    displayName: 'Application Rejected',
    description: 'Sent when admin rejects a clinic application',
    defaultVariables: ['clinicName', 'applicationId', 'reviewerName', 'reviewDate', 'rejectionReason', 'flaggedFields', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  CLINIC_APPLICATION_REAPPLIED: {
    eventKey: 'CLINIC_APPLICATION_REAPPLIED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.APPLICATION,
    category: 'application',
    displayName: 'Application Reapplied',
    description: 'Sent when a rejected clinic resubmits their application',
    defaultVariables: ['clinicName', 'applicationId', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'medium',
  },
  
  // Trial Lifecycle
  CLINIC_TRIAL_STARTED: {
    eventKey: 'CLINIC_TRIAL_STARTED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.TRIAL,
    category: 'trial',
    displayName: 'Trial Started',
    description: 'Sent when a clinic starts their free trial',
    defaultVariables: ['clinicName', 'trialStartDate', 'trialEndDate', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  CLINIC_TRIAL_ENDING_SOON: {
    eventKey: 'CLINIC_TRIAL_ENDING_SOON',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.TRIAL,
    category: 'trial',
    displayName: 'Trial Ending Soon',
    description: 'Sent 3 days before trial expires',
    defaultVariables: ['clinicName', 'trialDaysRemaining', 'trialEndDate', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  CLINIC_TRIAL_EXPIRED: {
    eventKey: 'CLINIC_TRIAL_EXPIRED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.TRIAL,
    category: 'trial',
    displayName: 'Trial Expired',
    description: 'Sent when a trial period has ended',
    defaultVariables: ['clinicName', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  // Subscription Lifecycle
  CLINIC_PLAN_UPGRADED: {
    eventKey: 'CLINIC_PLAN_UPGRADED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.SUBSCRIPTION,
    category: 'subscription',
    displayName: 'Plan Upgraded',
    description: 'Sent when a clinic upgrades their subscription package',
    defaultVariables: ['clinicName', 'previousPlan', 'planName', 'planPrice', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'medium',
  },
  
  // P3 Phase 4: Stripe Integration - Subscription Activation
  CLINIC_SUBSCRIPTION_ACTIVATED: {
    eventKey: 'CLINIC_SUBSCRIPTION_ACTIVATED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.SUBSCRIPTION,
    category: 'subscription',
    displayName: 'Subscription Activated',
    description: 'Sent when clinic payment is confirmed and subscription is active (webhook-triggered)',
    defaultVariables: ['clinicName', 'clinicEmail', 'planName', 'planPrice', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  // P3 Phase 4: Stripe Integration - Payment Failed
  CLINIC_PAYMENT_FAILED: {
    eventKey: 'CLINIC_PAYMENT_FAILED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.SUBSCRIPTION,
    category: 'subscription',
    displayName: 'Payment Failed',
    description: 'Sent when a subscription payment fails (webhook-triggered)',
    defaultVariables: ['clinicName', 'clinicEmail', 'planName', 'planPrice', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'critical',
  },
  
  // =========================================================================
  // CATEGORY B: PATIENT / CUSTOMER EVENTS
  // =========================================================================
  
  // Authentication
  PATIENT_ACCOUNT_CREATED: {
    eventKey: 'PATIENT_ACCOUNT_CREATED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.AUTH,
    category: 'auth',
    displayName: 'Account Created',
    description: 'Sent when a patient creates a new account',
    defaultVariables: ['patientName', 'patientFirstName', 'patientEmail', 'verificationLink', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  PATIENT_EMAIL_VERIFICATION: {
    eventKey: 'PATIENT_EMAIL_VERIFICATION',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.AUTH,
    category: 'auth',
    displayName: 'Email Verification',
    description: 'Sent to verify patient email address',
    defaultVariables: ['patientName', 'patientFirstName', 'verificationLink', 'verificationCode', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'critical',
  },
  
  PATIENT_PASSWORD_RESET_REQUEST: {
    eventKey: 'PATIENT_PASSWORD_RESET_REQUEST',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.AUTH,
    category: 'auth',
    displayName: 'Password Reset Request',
    description: 'Sent when patient requests a password reset',
    defaultVariables: ['patientName', 'patientFirstName', 'resetPasswordLink', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'critical',
  },
  
  PATIENT_PASSWORD_CHANGED: {
    eventKey: 'PATIENT_PASSWORD_CHANGED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.AUTH,
    category: 'auth',
    displayName: 'Password Changed',
    description: 'Sent when patient successfully changes their password',
    defaultVariables: ['patientName', 'patientFirstName', 'currentDate', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  // Intake & Matching
  PATIENT_INTAKE_SUBMITTED: {
    eventKey: 'PATIENT_INTAKE_SUBMITTED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.INTAKE,
    category: 'intake',
    displayName: 'Intake Submitted',
    description: 'Sent when patient completes their treatment intake form',
    defaultVariables: ['patientName', 'patientFirstName', 'intakeId', 'treatmentType', 'preferredDestination', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  PATIENT_INTAKE_UPDATED: {
    eventKey: 'PATIENT_INTAKE_UPDATED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.INTAKE,
    category: 'intake',
    displayName: 'Intake Updated',
    description: 'Sent when patient updates their intake information',
    defaultVariables: ['patientName', 'patientFirstName', 'intakeId', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'low',
  },
  
  PATIENT_MATCHING_STARTED: {
    eventKey: 'PATIENT_MATCHING_STARTED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.MATCHING,
    category: 'matching',
    displayName: 'Matching Started',
    description: 'Sent when the matching process begins for patient',
    defaultVariables: ['patientName', 'patientFirstName', 'treatmentType', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'medium',
  },
  
  PATIENT_MATCHES_FOUND: {
    eventKey: 'PATIENT_MATCHES_FOUND',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.MATCHING,
    category: 'matching',
    displayName: 'Matches Found',
    description: 'Sent when clinics matching patient criteria are found',
    defaultVariables: ['patientName', 'patientFirstName', 'matchCount', 'matchedClinics', 'treatmentType', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  PATIENT_NO_MATCH_FOUND: {
    eventKey: 'PATIENT_NO_MATCH_FOUND',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.MATCHING,
    category: 'matching',
    displayName: 'No Match Found',
    description: 'Sent when no clinics match patient criteria',
    defaultVariables: ['patientName', 'patientFirstName', 'treatmentType', 'preferredDestination', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'medium',
  },
  
  // Offers & Payments
  PATIENT_OFFER_RECEIVED: {
    eventKey: 'PATIENT_OFFER_RECEIVED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.OFFER,
    category: 'offer',
    displayName: 'Offer Received',
    description: 'Sent when patient receives a treatment offer from a clinic',
    defaultVariables: ['patientName', 'patientFirstName', 'clinicName', 'offerId', 'treatmentType', 'offerAmount', 'offerCurrency', 'offerExpiry', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  PATIENT_OFFER_REVISED: {
    eventKey: 'PATIENT_OFFER_REVISED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.OFFER,
    category: 'offer',
    displayName: 'Offer Revised',
    description: 'Sent when a clinic revises their offer',
    defaultVariables: ['patientName', 'patientFirstName', 'clinicName', 'offerId', 'offerAmount', 'offerCurrency', 'offerExpiry', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  PATIENT_OFFER_ACCEPTED: {
    eventKey: 'PATIENT_OFFER_ACCEPTED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.OFFER,
    category: 'offer',
    displayName: 'Offer Accepted',
    description: 'Sent when patient accepts a treatment offer',
    defaultVariables: ['patientName', 'patientFirstName', 'clinicName', 'offerId', 'treatmentType', 'offerAmount', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  PATIENT_DEPOSIT_REQUESTED: {
    eventKey: 'PATIENT_DEPOSIT_REQUESTED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.PAYMENT,
    category: 'payment',
    displayName: 'Deposit Requested',
    description: 'Sent when clinic requests deposit payment',
    defaultVariables: ['patientName', 'patientFirstName', 'clinicName', 'depositAmount', 'depositDueDate', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  PATIENT_DEPOSIT_RECEIVED: {
    eventKey: 'PATIENT_DEPOSIT_RECEIVED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.PAYMENT,
    category: 'payment',
    displayName: 'Deposit Received',
    description: 'Sent when patient deposit payment is confirmed',
    defaultVariables: ['patientName', 'patientFirstName', 'clinicName', 'depositAmount', 'paymentMethod', 'platformUrl', 'supportEmail'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  
  // =========================================================================
  // CATEGORY C: INTERNAL / OPS EVENTS
  // =========================================================================
  
  ADMIN_NEW_HIGH_VALUE_PATIENT: {
    eventKey: 'ADMIN_NEW_HIGH_VALUE_PATIENT',
    audience: EMAIL_AUDIENCE.INTERNAL,
    triggerSource: TRIGGER_SOURCE.ADMIN,
    category: 'internal',
    displayName: 'High-Value Patient Alert',
    description: 'Internal alert for high-value patient registration',
    defaultVariables: ['patientName', 'patientEmail', 'patientCountry', 'treatmentType', 'estimatedValue', 'leadScore'],
    isUserEditable: false, // Internal templates not editable
    isActiveByDefault: true,
    priority: 'high',
  },
  
  ADMIN_MULTIPLE_REJECTION_ALERT: {
    eventKey: 'ADMIN_MULTIPLE_REJECTION_ALERT',
    audience: EMAIL_AUDIENCE.INTERNAL,
    triggerSource: TRIGGER_SOURCE.ADMIN,
    category: 'internal',
    displayName: 'Multiple Rejection Alert',
    description: 'Internal alert when multiple applications are rejected in short time',
    defaultVariables: ['alertReason', 'reviewerName', 'currentDate'],
    isUserEditable: false, // Internal templates not editable
    isActiveByDefault: true,
    priority: 'medium',
  },
  
  SALES_REP_ASSIGNED_TO_LEAD: {
    eventKey: 'SALES_REP_ASSIGNED_TO_LEAD',
    audience: EMAIL_AUDIENCE.INTERNAL,
    triggerSource: TRIGGER_SOURCE.SALES,
    category: 'internal',
    displayName: 'Sales Rep Assigned',
    description: 'Notification to sales rep when assigned to a new lead',
    defaultVariables: ['salesRepName', 'salesRepEmail', 'patientName', 'treatmentType', 'leadScore', 'clinicName'],
    isUserEditable: true, // Issue 7 — Owner needs to edit copy of this canonical event
    isActiveByDefault: true,
    priority: 'high',
  },

  // ---------------------------------------------------------------------------
  // Issue 7 — Canonical events already mapped in the backend
  // `services/notification_event_mapping.py`. They MUST appear in the admin
  // registry so the Owner can edit copy per locale. Backend seed already
  // ships EN + TR bodies for each of these keys.
  // ---------------------------------------------------------------------------
  QUOTE_SENT: {
    eventKey: 'QUOTE_SENT',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.OFFER,
    category: 'offer',
    displayName: 'Quote sent',
    description: 'Sent to the patient when a clinic issues a new price quote',
    defaultVariables: ['patient_name', 'clinic_name', 'quote_reference', 'amount', 'currency', 'quote_url'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  QUOTE_ACCEPTED: {
    eventKey: 'QUOTE_ACCEPTED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.OFFER,
    category: 'offer',
    displayName: 'Quote accepted',
    description: 'Confirmation when the patient accepts a quote',
    defaultVariables: ['patient_name', 'clinic_name', 'quote_reference', 'booking_url'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
  QUOTE_EXPIRED: {
    eventKey: 'QUOTE_EXPIRED',
    audience: EMAIL_AUDIENCE.PATIENT,
    triggerSource: TRIGGER_SOURCE.OFFER,
    category: 'offer',
    displayName: 'Quote expired',
    description: 'Sent to the patient when a quote reaches its expiry date',
    defaultVariables: ['patient_name', 'clinic_name', 'quote_reference'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'normal',
  },
  CONTACT_NOTIFICATION: {
    eventKey: 'CONTACT_NOTIFICATION',
    audience: EMAIL_AUDIENCE.INTERNAL,
    triggerSource: TRIGGER_SOURCE.INTAKE,
    category: 'ops',
    displayName: 'Contact form notification',
    description: 'Sent to TreatX ops when someone submits the public contact form',
    defaultVariables: ['contact_name', 'contact_email', 'subject', 'message'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'normal',
  },
  AFTERCARE_ESCALATED: {
    eventKey: 'AFTERCARE_ESCALATED',
    audience: EMAIL_AUDIENCE.CLINIC,
    triggerSource: TRIGGER_SOURCE.INTAKE,
    category: 'ops',
    displayName: 'Aftercare escalation',
    description: 'Alert when a patient aftercare interaction is escalated to the clinic',
    defaultVariables: ['patient_name', 'booking_reference', 'reason', 'action_url'],
    isUserEditable: true,
    isActiveByDefault: true,
    priority: 'high',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all events for a specific audience
 */
export const getEventsByAudience = (audience) => {
  return Object.values(EMAIL_EVENTS).filter(event => event.audience === audience);
};

/**
 * Get all events for a specific trigger source
 */
export const getEventsByTriggerSource = (triggerSource) => {
  return Object.values(EMAIL_EVENTS).filter(event => event.triggerSource === triggerSource);
};

/**
 * Get all events for a specific category
 */
export const getEventsByCategory = (category) => {
  return Object.values(EMAIL_EVENTS).filter(event => event.category === category);
};

/**
 * Get all user-editable events (for admin UI)
 */
export const getUserEditableEvents = () => {
  return Object.values(EMAIL_EVENTS).filter(event => event.isUserEditable);
};

/**
 * Get event by key
 */
export const getEventByKey = (eventKey) => {
  return EMAIL_EVENTS[eventKey] || null;
};

/**
 * Get variables available for a specific event
 */
export const getEventVariables = (eventKey) => {
  const event = EMAIL_EVENTS[eventKey];
  if (!event) return [];
  
  return event.defaultVariables.map(varKey => ({
    key: varKey,
    ...TEMPLATE_VARIABLES[varKey],
  }));
};

/**
 * Get variables available for a specific audience
 */
export const getVariablesByAudience = (audience) => {
  return Object.entries(TEMPLATE_VARIABLES)
    .filter(([_, config]) => config.scope.includes(audience))
    .map(([key, config]) => ({ key, ...config }));
};

/**
 * Validate that all required variables are present in template
 */
export const validateTemplateVariables = (eventKey, templateContent) => {
  const event = EMAIL_EVENTS[eventKey];
  if (!event) return { valid: false, error: 'Invalid event key' };
  
  const missingVars = [];
  event.defaultVariables.forEach(varKey => {
    if (!templateContent.includes(`{{${varKey}}}`)) {
      missingVars.push(varKey);
    }
  });
  
  return {
    valid: missingVars.length === 0,
    missingVariables: missingVars,
  };
};

/**
 * Map old template IDs to new event keys (for migration)
 */
export const TEMPLATE_ID_TO_EVENT_KEY_MAP = {
  'tpl_application_submitted': 'CLINIC_APPLICATION_SUBMITTED',
  'tpl_application_approved': 'CLINIC_APPLICATION_APPROVED',
  'tpl_application_rejected': 'CLINIC_APPLICATION_REJECTED',
  'tpl_application_reapplied': 'CLINIC_APPLICATION_REAPPLIED',
  'tpl_trial_started': 'CLINIC_TRIAL_STARTED',
  'tpl_trial_ending_soon': 'CLINIC_TRIAL_ENDING_SOON',
  'tpl_trial_expired': 'CLINIC_TRIAL_EXPIRED',
  'tpl_plan_upgraded': 'CLINIC_PLAN_UPGRADED',
};

/**
 * Get event key from old template ID
 */
export const getEventKeyFromTemplateId = (templateId) => {
  return TEMPLATE_ID_TO_EVENT_KEY_MAP[templateId] || null;
};

// =============================================================================
// SUMMARY STATISTICS (for UI display)
// =============================================================================
export const getEventStatistics = () => {
  const allEvents = Object.values(EMAIL_EVENTS);
  
  return {
    total: allEvents.length,
    byAudience: {
      [EMAIL_AUDIENCE.CLINIC]: getEventsByAudience(EMAIL_AUDIENCE.CLINIC).length,
      [EMAIL_AUDIENCE.PATIENT]: getEventsByAudience(EMAIL_AUDIENCE.PATIENT).length,
      [EMAIL_AUDIENCE.INTERNAL]: getEventsByAudience(EMAIL_AUDIENCE.INTERNAL).length,
    },
    byCategory: {
      application: getEventsByCategory('application').length,
      trial: getEventsByCategory('trial').length,
      subscription: getEventsByCategory('subscription').length,
      auth: getEventsByCategory('auth').length,
      intake: getEventsByCategory('intake').length,
      matching: getEventsByCategory('matching').length,
      offer: getEventsByCategory('offer').length,
      payment: getEventsByCategory('payment').length,
      internal: getEventsByCategory('internal').length,
    },
    userEditable: getUserEditableEvents().length,
    activeByDefault: allEvents.filter(e => e.isActiveByDefault).length,
  };
};

export default EMAIL_EVENTS;
